import { InputHTMLAttributes, ReactNode, useEffect, useState } from 'react'
import clsx from 'clsx'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { HamburgerMenuIcon, MoonIcon, SunIcon } from '@radix-ui/react-icons'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'

import PanelBackgroundImage from '@/assets/Paytrie_Patterns_arrow_snow.png'
import PoweredByImage from '@/assets/powered-by-label.svg'
import InteracLogo from '@/assets/interac.png'
import PaytrieIcon from '@/assets/paytrie-nav-hr.svg'
import { IS_FUTURA } from '@/constants/futura'
import { usePageStateStore, useSignupStore } from '@/reducers'
// import darkModeMoon from '@/assets/darkModeMoon.svg'
// import lightModeSun from '@/assets/lightModeSun.svg'

export const RowAlign = styled.div`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  text-align: center;
  justify-content: space-between;
  gap: 5px;
`

export const BackButton = styled.button`
  font-size: 20px;
  text-align: left;
  outline-offset: 4px;
  &[data-testid='btn-navigation-back'] {
    /* Preserve button's appearance while adding support for data-testid */
  }
`

export const LogoutButton = styled.button`
  font-size: 20px;
  text-align: right;
  outline-offset: 4px;
  padding-top: 20px;
  &[data-testid='btn-auth-logout'] {
    /* Preserve button's appearance while adding support for data-testid */
  }
`

export const StrikeLine = styled.div`
  content: '';
  flex: 1 1;
  border-bottom: 1px solid;
  margin: auto;
`

export const LineThroughText = styled.div`
  width: 100%;
  font-size: 0.5em;
  flex-direction: row;
  padding-top: 1em;
  padding-bottom: 1em;
  display: flex;
  flex: 1;
  &:before,
  &:after {
    flex: 1;
    content: '';
    border-top: 1px solid;
    height: 0;
    margin: 5px;
  }
`

export const SmallText = styled.div`
  text-align: right;
  font-size: 12px;
`

export const LeftAlignSmallText = styled.div`
  text-align: left;
  font-size: 12px;
  padding-bottom: 4em;
`

export const LeftAlignSmallerText = styled.div`
  text-align: left;
  font-size: 10px;
  padding-bottom: 4em;
`

export const CenterAlignedSmallText = styled.div`
  text-align: center;
  font-size: 12px;
  padding-bottom: 4em;
`

export const TocSmallText = styled.div`
  text-align: left;
  font-size: 10px;
`

export const NoAccountText = styled(SmallText)`
  padding-bottom: 4em;
  text-align: left;
`

export const ReferralAccountText = styled(SmallText)`
  text-align: left;
`

export const PoweredBy = styled(SmallText)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`

export const TopIcon = styled.div`
  padding-top: 30px;
  padding-bottom: 100px;
  height: 22px;
  width: 100%;
  display: flex;
  text-align: right;
  margin-right: 0px;
  flex-direction: row-reverse;
`

export const Header2 = styled.div`
  color: #fff;
  /* web/subhead */

  font-family: 'PP Object Sans';
  font-style: normal;
  font-weight: 700;
  font-size: 16px;
  line-height: 110%;
  padding-bottom: 12px;
  /* or 18px */

  letter-spacing: 0.07em;
  text-transform: uppercase;
  font-feature-settings: 'ss01' on;
`

export const LeftAlign = styled.div`
  text-align: left;
  align: left;
`

export const BlockText = styled.div`
  font-family: 'PP Object Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 120%;
  /* or 19px */

  font-feature-settings: 'ss01' on;

  /* web/ snow */

  color: #ffffff;
`

export const ErrorBlockText = styled(BlockText)`
  color: #f33;
`

export const InputText = styled.input`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  margin-top: 10px;
  padding-bottom: 4px;
  width: 100%;
  background-color: transparent;
  gap: 14px;
  border: none;
  border-radius: 0;
  border-bottom: 2px solid var(--rock);
  height: 32px;
  font-family: 'PP Object Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  user-select: none;
  box-shadow: none;
  outline: none;
  &:user-invalid {
    border-color: red;
  }
  &:-webkit-autofill,
  &:-webkit-autofill:focus {
    transition:
      background-color 0s 600000s,
      color 0s 600000s !important;
  }
  &[type='number'] {
    -moz-appearance: textfield;
  }
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`

export interface CheckboxProps {
  labelText: string
  isChecked: boolean
  setIsChecked: (arg0: boolean) => void
}

export function Checkbox({
  labelText,
  isChecked,
  setIsChecked,
}: CheckboxProps) {
  return (
    <BlockText>
      <label data-testid={`container-checkbox-${labelText}`}>
        <input
          type="checkbox"
          checked={isChecked}
          onChange={() => {
            setIsChecked(!isChecked)
          }}
          data-testid={`check-checkbox-${labelText}`}
        />
        {labelText}
      </label>
    </BlockText>
  )
}

export type TextInputProps = {
  placeholder?: string
  pattern?: string
} & InputHTMLAttributes<HTMLInputElement>

export function TextInput({ placeholder, pattern, ...props }: TextInputProps) {
  return (
    <InputText
      type={pattern}
      placeholder={placeholder}
      data-testid={`input-form-${placeholder || pattern || 'default'}`}
      {...props}
      className={clsx(
        props.className,
        'text-pebble dark:text-snow focus:text-clementine dark:focus:text-sun'
      )}
    />
  )
}

export function PoweredByLabel() {
  return (
    <PoweredBy data-testid="container-footer-powered-by">
      <div>
        <img
          src={PoweredByImage}
          height={26}
          className="dark:invert invert-0 h-[26px]"
        />
      </div>
    </PoweredBy>
  )
}

export function InteracLabel() {
  const { t } = useTranslation()
  return (
    <PoweredBy data-testid="container-payment-interac">
      <div className="text-gravel dark:text-rock" style={{ fontSize: '16px' }}>
        {t('widget.payment.processedBy')}
      </div>
      <div className="m-2">
        <img width={40} src={InteracLogo} />
      </div>
    </PoweredBy>
  )
}

export function PaytrieTopIcon({ className }: { className?: string }) {
  return (
    <img
      src={PaytrieIcon}
      className={clsx('h-[25px] w-[25px]', 'dark:invert invert-0', className)}
      data-testid="icon-brand-paytrie"
    />
  )
}

export const PanelBackground = styled.div`
  position: relative;
  &:after {
    background: url(${PanelBackgroundImage}) no-repeat;
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: -1;
    background-size: cover;
    background-position: center;
  }
`

export const panelClass = clsx([
  'bg-center',
  'h-[100px]',
  'items-center',
  'md:items-start',
  'md:min-h-[700px]',
  'md:h-max',
  'flex',
  'flex-row',
  'md:flex-col',
  'justify-between',
  'md:justify-start',
  'md:gap-10',
  'md:py-10',
  'px-5',
  'md:min-w-[350px]',
  'md:w-auto',
  'md:max-w-[450px]',
  'after:invert after:dark:invert-0',
])

export const LoginHeader = ({
  children,
  hideBack,
}: {
  children: ReactNode
  hideBack?: boolean
}) => {
  const { t } = useTranslation()
  const reset = useSignupStore.use.reset()
  const navigate = useNavigate()
  const urlParams = new URLSearchParams(window.location.search)
  const emailParams = urlParams.get('email') || ''

  useEffect(() => {
    // reset the form on unmount
    return () => reset()
  }, [])

  return (
    <PanelBackground
      className={panelClass}
      data-testid="container-auth-login-header"
    >
      {!IS_FUTURA && !hideBack && (
        <BackButton
          onClick={() => {
            if (
              window.location.pathname === '/SignupKYC' &&
              emailParams?.length > 0
            ) {
              console.log('GO BACK SPECIFICALLY TO OCCUPATION')
              navigate(-5) // redirect to oliu which itself does a couple of redirects that all push entries to history queue
            } else {
              console.log('HISTORY GO BACK')
              navigate(-1)
            }
          }}
          className="absolute top-8 md:top-5 left-5 md:left-10"
          data-testid="btn-navigation-back"
        >
          {t('widget.navigation.back')}
        </BackButton>
      )}
      <div className="hidden md:block py-5 md:py-10 md:px-4">{children}</div>
      {/* {window.self !== window.top && (
        <div className="absolute top-10 right-0">
          <DarkModeMenu />
        </div>
      )} */}
    </PanelBackground>
  )
}

export const ProfileHeader = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <PanelBackground
      className={`${panelClass} relative gap-10 items-center w-full`}
      data-testid="container-profile-header"
    >
      <BackButton
        onClick={() => {
          navigate('/')
        }}
        className="absolute top-8 md:top-5 left-5 md:left-10"
        data-testid="btn-navigation-back"
      >
        {t('widget.navigation.back')}
      </BackButton>

      <PaytrieTopIcon className="absolute top-8 right-5 md:hidden" />
    </PanelBackground>
  )
}

export const PanelEmpty = styled.div`
  position: relative;
  &:after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 100%;
    z-index: -1;
    background-size: cover;
    background-position: center;
  }
`

export const CorpEnrollHeader = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <PanelEmpty
      className={panelClass}
      data-testid="container-enrollment-corp-header"
    >
      <BackButton
        onClick={() => {
          navigate(-1)
        }}
        className="absolute top-8 md:top-5 left-5 md:left-10"
        data-testid="btn-navigation-back"
      >
        {t('widget.navigation.back')}
      </BackButton>
      <div className="hidden md:block py-5 md:py-10 md:px-4">{children}</div>
    </PanelEmpty>
  )
}

export const EllipsisAnimation = styled.div`
  position: relative;
  &:after {
    position: absolute;
    display: inline-block;
    animation: dotty steps(1, end) 1s infinite;
    content: '';
  }

  @keyframes dotty {
    0% {
      content: '';
    }
    25% {
      content: '.';
    }
    50% {
      content: '..';
    }
    75% {
      content: '...';
    }
    100% {
      content: '';
    }
  }
`

const dropdownMenuItemStyle = {
  cursor: 'pointer',
  textAlign: 'left',
} as const

export const DropdownMenuSelect = () => {
  const { i18n, t } = useTranslation()
  const navigate = useNavigate()
  const clearAuthToken = usePageStateStore.use.clearAuthToken()
  const [isLoggedIn, setIsLoggedIn] = useState(false)

  const getInitialLang = () => {
    const savedLang = localStorage.getItem('lang')
    if (savedLang !== null) {
      return savedLang
    }
    return 'en'
  }

  const getInitialMode = () => {
    const savedMode = localStorage.getItem('darkMode')
    if (savedMode !== null) {
      return savedMode === 'true'
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }

  const [darkMode, setDarkMode] = useState(getInitialMode)
  const [lang, setLang] = useState(getInitialLang)

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }

    const handleMessageFromParent = (event: {
      data: { darkMode: boolean | ((prevState: boolean) => boolean) }
    }) => {
      if (event.data && typeof event.data.darkMode === 'boolean') {
        setDarkMode(event.data.darkMode)
        localStorage.setItem('darkMode', String(event.data.darkMode))
      }
    }

    window.addEventListener('message', handleMessageFromParent)
    return () => window.removeEventListener('message', handleMessageFromParent)
  }, [darkMode])

  useEffect(() => {
    const authToken =
      localStorage.getItem('authToken') || sessionStorage.getItem('authToken')
    setIsLoggedIn(!!authToken)
  }, [])

  useEffect(() => {
    const syncDarkModeAcrossTabs = (event: StorageEvent) => {
      if (event.key === 'darkMode') {
        setDarkMode(event.newValue === 'true')
      }
    }
    window.addEventListener('storage', syncDarkModeAcrossTabs)
    return () => {
      window.removeEventListener('storage', syncDarkModeAcrossTabs)
    }
  }, [])

  const handleSignOut = () => {
    clearAuthToken()
    setIsLoggedIn(false)
    // navigate('/');
    window.location.href = import.meta.env.VITE_APPLICATION_URL
  }

  const toggleDarkMode = () => {
    const newMode = !darkMode
    setDarkMode(newMode)
    localStorage.setItem('darkMode', String(newMode))
    window.parent.postMessage({ darkMode: newMode }, '*')
  }

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    window.addEventListener('message', (event) => {
      if (event.data && typeof event.data.darkMode === 'boolean') {
        const darkMode = event.data.darkMode
        document.documentElement.classList.toggle('dark', darkMode)
        localStorage.setItem('darkMode', String(darkMode))
      }
    })
  }, [darkMode])

  if (window.self !== window.top) {
    return null
  }

  const toggleLangSupport = () => {
    const newLang = lang === 'en' ? 'fr' : 'en'
    setLang(newLang)
    localStorage.setItem('lang', newLang)
    i18n.changeLanguage(newLang)
  }

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          className="IconButton"
          aria-label="customize options"
          data-testid="select-form-dropdown-menu"
        >
          <HamburgerMenuIcon className="w-8 h-8" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="bg-white dark:bg-pebble min-w-[220px] p-3 shadow-md"
          style={{
            borderRadius: '0',
            boxShadow:
              '0px 10px 38px -10px rgba(22, 23, 24, 0.35), 0px 10px 20px -15px rgba(22, 23, 24, 0.2)',
            animationDuration: '400ms',
            animationTimingFunction: 'cubic-bezier(0.16, 1, 0.3, 1)',
            textAlign: 'left',
          }}
          sideOffset={5}
        >
          {!IS_FUTURA &&
            (!isLoggedIn ? (
              <>
                <DropdownMenu.Item
                  className="DropdownMenuItem my-1"
                  style={dropdownMenuItemStyle}
                  onSelect={() => {
                    navigate('/EasyLogin')
                  }}
                >
                  {t('widget.auth.signIn')}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  className="DropdownMenuItem my-1"
                  style={dropdownMenuItemStyle}
                  onSelect={() => navigate('/SignupEmail')}
                >
                  {t('widget.auth.register')}
                </DropdownMenu.Item>
              </>
            ) : (
              <>
                <DropdownMenu.Item
                  className="DropdownMenuItem my-1"
                  style={dropdownMenuItemStyle}
                  onSelect={() => navigate('/Dashboard')}
                >
                  {t('widget.navigation.dashboard')}
                </DropdownMenu.Item>

                <DropdownMenu.Item
                  className="DropdownMenuItem my-1"
                  style={dropdownMenuItemStyle}
                  onSelect={handleSignOut}
                >
                  {t('widget.auth.signOut')}
                </DropdownMenu.Item>
              </>
            ))}

          <DropdownMenu.CheckboxItem
            style={dropdownMenuItemStyle}
            className="DropdownMenuCheckboxItem my-1"
            checked={lang === 'en'}
            onCheckedChange={toggleLangSupport}
          >
            {lang === 'en' ? <p>FR</p> : <p>EN</p>}
          </DropdownMenu.CheckboxItem>

          <DropdownMenu.CheckboxItem
            style={dropdownMenuItemStyle}
            className="DropdownMenuCheckboxItem my-1"
            checked={darkMode}
            onCheckedChange={toggleDarkMode}
          >
            {darkMode ? (
              <SunIcon className="w-5 h-5" />
            ) : (
              <MoonIcon className="w-5 h-5" />
            )}
          </DropdownMenu.CheckboxItem>
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  )
}

export default DropdownMenuSelect

// Suggestions
// 1. Consider adding more explicit typing for component props throughout the file
// 2. BackButton and similar navigation components could be consolidated into a reusable NavButton component
// 3. Text components (SmallText, LeftAlignSmallText, etc.) could be unified with a configurable Text component
// 4. Add aria-labels to improve accessibility across components
// 5. TextInput could benefit from validation states (error, success) with appropriate visual feedback

/*
IMPROVEMENT SUGGESTIONS:
1. The TextInput component should support a specific data-testid prop to allow for more meaningful test identifiers
2. ErrorBlockText components lack accessibility attributes like aria-live for error announcements
3. Consider adding form context to manage form state instead of individual components managing their own state
4. Dropdown components could benefit from better keyboard navigation support
5. UI components are tightly coupled with navigation logic, consider separating concerns
6. Add explicit type definitions for all props to improve type safety
7. Consider using a form library like react-hook-form for better form validation and state management
8. Add accessibility attributes like aria-label to interactive elements
9. Clean up commented out code
10. Consider adding a11y testing with tools like axe-core
*/
